import * as React from 'react';
import { Paper, PromptConfirmation } from 'ui';

import { withPrompt, PromptProps } from 'app/components';
import { STATE } from 'app/constants';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { safeNumericString } from 'app/infrastructure/data/sanitize';
import { containerFactory, DefaultContainerProps } from 'app/infrastructure/redux';
import { hasTranslationsLoaded, translationChanged } from 'app/infrastructure/translate/selectors';
import { OptionsProps, withOptions } from 'app/components/options-handler';
import * as navigate from 'app/area/shared/navigation';

import { getTextConfigurationAccess } from '../common/get-text-configuration-access';
import { textConfigurationTranslation } from '../constants';
import { TextConfigurationState } from '../types-store';

import { TextFormRenderer } from './components/text-form-renderer';
import { hasChanges } from './utils';
import { actionCreators } from './actions';

export type TextConfigurationFormProps = DefaultContainerProps
  & OptionsProps
  & PromptProps
  & TextConfigurationState
  & typeof actionCreators;

class TextConfigurationFormComponent extends React.PureComponent<TextConfigurationFormProps> {
  public static statePath: string = STATE.textConfiguration;

  public componentDidMount() {
    this.props.loadLanguageOptions();

    if (hasTranslationsLoaded(this.props.translation)) {
      const textId = safeNumericString(this.props.params.textId);
      const textSet = safeNumericString(this.props.params.textSet);
      this.initForm(textId, textSet);
    }
  }

  public componentDidUpdate(prevProps: TextConfigurationFormProps) {
    const textId = safeNumericString(this.props.params.textId);
    const textSet = safeNumericString(this.props.params.textSet);
    const prevTextId = safeNumericString(prevProps.params.textId);
    const prevTextSet = safeNumericString(prevProps.params.textSet);

    if (textId !== prevTextId || textSet !== prevTextSet ||
      translationChanged(this.props.translation, prevProps.translation)) {
      this.initForm(textId, textSet);
    }
  }

  public componentWillUnmount() {
    this.props.clearForm();
  }

  private initForm(textId: number | '', textSet: number | '') {
    const { sharedState, loadText, clearForm, displayError, translation } = this.props;

    const textConfigurationAccess = getTextConfigurationAccess(sharedState);
    if (!textConfigurationAccess) {
      displayError(translation.no_access_to_text_configuration);
      navigate.toStartupView(true);
      return;
    }

    if (isDefinedIncludingZero(textId)) {
      loadText(Number(textId), Number(textSet));
    } else {
      clearForm();
    }
  }

  public render() {

    const { sharedState, form, translation, locale,
      deleteText, saveText,
      options, loadOptions,
      onTextSetChange, onTextCollectionChange,
      onTextChange, copyTextToAllTranslations,
      updateValidation, displayError
    } = this.props;

    const mainLanguage = sharedState && sharedState.language
      ? sharedState.language.value
      : undefined;

    return <div className="entity-view">
      <Paper componentId="code-base-paper" className={undefined}>
        {!!this.props.shouldDisplayModal
          ? <PromptConfirmation
            title={translation.cancel_changes}
            description={translation.are_you_sure}
            continueText={translation.undo_all}
            cancelText={translation.go_back}
            onContinue={this.props.continue}
            onCancel={this.props.cancel} />
          : ''}
        <TextFormRenderer
          formState={form}
          mainLanguage={mainLanguage}
          development={sharedState?.access?.adminPanel?.develop || false}
          saveText={saveText}
          deleteText={deleteText}
          onTextSetChange={onTextSetChange}
          onTextCollectionChange={onTextCollectionChange}
          options={options}
          loadOptions={loadOptions}
          onTextChange={onTextChange}
          copyTextToAllTranslations={copyTextToAllTranslations}
          updateValidation={updateValidation}
          displayError={displayError}
          translation={translation}
          locale={locale} />
      </Paper>
    </div>;
  }
}

const selectors = {
  shouldPrompt: ({ form: { textItem, originalTextItem } }: TextConfigurationState) =>
    hasChanges(textItem, originalTextItem)
};

const container = containerFactory(
  withPrompt(withOptions(TextConfigurationFormComponent)),
  actionCreators, selectors, textConfigurationTranslation
);

export { container as TextConfigurationForm };
